import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography, 
  Divider,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { 
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  PlayArrow as PlayArrowIcon,
  Search as SearchIcon,
  Psychology as AIIcon,
  Group as CollaborationIcon,
  Article as ArticleIcon,
  Category as CategoryIcon,
  Lightbulb as InsightIcon,
  Chat as ChatIcon,
  Share as ShareIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  MenuBook as MenuBookIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useTheme } from '../../utils/ThemeContext';
import { useInteractiveTutorial } from '../../utils/InteractiveTutorialContext';
import { BUTTON_COLORS } from '../../config/buttonStyles';

const TutorialsMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { 
    startTutorial, 
    isTutorialCompleted, 
    isTutorialUnlocked 
  } = useInteractiveTutorial();

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTutorialClick = (tutorialId, route) => {
    handleClose();
    // Navigate to the page first
    navigate(route);
    // Start tutorial after a delay to ensure page has fully loaded and rendered
    setTimeout(() => {
      startTutorial(tutorialId);
    }, 800);
  };

  const getTutorialStatus = (tutorialId) => {
    const completed = isTutorialCompleted(tutorialId);
    const unlocked = isTutorialUnlocked(tutorialId);
    return { completed, unlocked };
  };

  const renderTutorialItem = (tutorialId, label, icon, route) => {
    const { completed, unlocked } = getTutorialStatus(tutorialId);
    
    return (
      <MenuItem
        onClick={() => handleTutorialClick(tutorialId, route)}
        disabled={!unlocked}
        sx={{
          color: 'var(--text-primary)',
          padding: '0.75rem 1rem',
          margin: '0 0.25rem',
          borderRadius: '4px',
          opacity: unlocked ? 1 : 0.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: unlocked 
              ? 'rgba(108, 48, 130, 0.1) !important' 
              : 'transparent',
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: '36px' }}>
          {React.cloneElement(icon, { 
            sx: { fontSize: '20px', color: 'var(--neon-cyan)' } 
          })}
        </ListItemIcon>
        <ListItemText 
          primary={label}
          primaryTypographyProps={{
            variant: 'body2',
            sx: { fontSize: '14px', fontWeight: 500 }
          }}
        />
        <ListItemIcon sx={{ minWidth: '28px', justifyContent: 'flex-end' }}>
          {!unlocked ? (
            <LockIcon sx={{ fontSize: '16px', color: 'var(--text-muted)' }} />
          ) : completed ? (
            <CheckCircleIcon sx={{ fontSize: '16px', color: 'var(--success)' }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: '16px', color: 'var(--neon-cyan)' }} />
          )}
        </ListItemIcon>
      </MenuItem>
    );
  };

  const renderSectionHeader = (title) => (
    <MenuItem
      sx={{
        cursor: 'default',
        pointerEvents: 'none',
        padding: '0.5rem 1rem',
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }}
    >
      <Typography 
        variant="caption" 
        sx={{ 
          color: 'var(--text-muted)',
          fontWeight: '700',
          fontSize: '11px',
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}
      >
        {title}
      </Typography>
    </MenuItem>
  );

  // Count completed tutorials
  const getTutorialCounts = () => {
    const allTutorials = [
      'tutorial_search_query',
      'tutorial_article_annotation',
      'tutorial_category_management',
      'research_portal_tutorial',
      'tutorial_ai_select_articles',
      'tutorial_collab_start_chat'
    ];
    
    const completed = allTutorials.filter(id => isTutorialCompleted(id)).length;
    const total = allTutorials.length;
    
    return { completed, total };
  };

  const { completed, total } = getTutorialCounts();

  return (
    <>
      <button
        onClick={handleClick}
        className="btn-modern"
        data-tutorial="tutorials-button"
        style={{
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          borderRadius: '4px',
          fontWeight: '500',
          fontSize: '0.95rem',
          transition: 'all 0.2s ease',
          background: isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight,
          color: `${BUTTON_COLORS.textPrimary} !important`,
          border: '1px solid transparent',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = BUTTON_COLORS.hoverFill;
          e.currentTarget.style.borderColor = BUTTON_COLORS.borderHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight;
          e.currentTarget.style.borderColor = 'transparent';
        }}
      >
        <SchoolIcon style={{ fontSize: '1.2rem', color: BUTTON_COLORS.textPrimary }} />
        <span>Tutorials</span>
        {completed > 0 && (
          <Chip 
            label={`${completed}/${total}`}
            size="small"
            sx={{
              ml: 0.5,
              height: '18px',
              fontSize: '10px',
              fontWeight: 700,
              backgroundColor: 'rgba(70, 130, 48, 0.2)',
              color: 'var(--success)',
              border: '1px solid rgba(70, 130, 48, 0.5)',
            }}
          />
        )}
      </button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            minWidth: '320px',
            maxHeight: '600px',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--glass-border)',
              borderRadius: '3px',
            },
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <MenuItem
          sx={{
            cursor: 'default',
            pointerEvents: 'none',
            padding: '1rem',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'var(--text-primary)',
              fontWeight: '700',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <SchoolIcon sx={{ color: 'var(--neon-cyan)' }} />
            Interactive Tutorials
          </Typography>
        </MenuItem>

        <Divider sx={{ borderColor: 'var(--glass-border)' }} />

        {/* Getting Started */}
        {renderSectionHeader('🎓 Getting Started (Start Here!)')}
        {renderTutorialItem('research_portal_tutorial', 'Welcome & Complete Your Profile', <SchoolIcon />, '/homepage')}

        <Divider sx={{ borderColor: 'var(--glass-border)', my: 1 }} />

        {/* Research Portal Tutorials */}
        {renderSectionHeader('🔬 Research Portal')}
        {renderTutorialItem('tutorial_search_query', 'Creating Your First Search Query', <SearchIcon />, '/homepage')}
        {renderTutorialItem('tutorial_article_annotation', 'Article Annotation & Highlights', <ArticleIcon />, '/homepage')}
        {renderTutorialItem('tutorial_category_management', 'Category Management & Organization', <CategoryIcon />, '/homepage')}

        <Divider sx={{ borderColor: 'var(--glass-border)', my: 1 }} />

        {/* AI Assistant Tutorials */}
        {renderSectionHeader('🤖 AI Assistant')}
        {renderTutorialItem('tutorial_ai_select_articles', 'AI Assistant: Select Articles & Chat', <AIIcon />, '/ai-assistant')}

        <Divider sx={{ borderColor: 'var(--glass-border)', my: 1 }} />

        {/* Collaboration Tutorials */}
        {renderSectionHeader('👥 Collaboration')}
        {renderTutorialItem('tutorial_collab_start_chat', 'Collaboration: Chat & Share', <GroupIcon />, '/collaboration')}
      </Menu>
    </>
  );
};

export default TutorialsMenu;

